import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  Avatar,
  Stack,
  Chip,
  Paper,
  Fab,
  Zoom,
} from '@mui/material';
import { motion } from 'framer-motion';
import CodeIcon from '@mui/icons-material/Code';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import MobileIcon from '@mui/icons-material/PhoneIphone';
import WebIcon from '@mui/icons-material/Web';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import CloudIcon from '@mui/icons-material/Cloud';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WorkIcon from '@mui/icons-material/CheckCircle';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import BuildIcon from '@mui/icons-material/Build';
import LaunchIcon from '@mui/icons-material/Launch';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionCard = motion(Card);

const features = [
  {
    icon: <PhoneIphoneIcon sx={{ fontSize: 40 }} />,
    title: 'Mobile Excellence',
    description: 'Native iOS and Android apps that users love',
    color: '#14b8a6',
  },
  {
    icon: <WebIcon sx={{ fontSize: 40 }} />,
    title: 'Web Innovation',
    description: 'Modern web applications with cutting-edge tech',
    color: '#0891b2',
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: 'Lightning Fast',
    description: 'Optimized performance for the best experience',
    color: '#f97316',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: 'Secure & Reliable',
    description: 'Enterprise-grade security and reliability',
    color: '#10b981',
  },
];

const stats = [
  { number: '20+', label: 'Projects' },
  { number: '98%', label: 'Satisfaction' },
  { number: '24/7', label: 'Support' },
];

const technologies = [
  'Shopify Apps', 'Shopify Plus', 'Liquid Templates', 'Shopify CLI', 'Polaris Design System',
  'Shopify GraphQL', 'Shopify REST API', 'Shopify Webhooks', 'App Extensions', 'Checkout Extensions',
  'React.js', 'Next.js', 'TypeScript', 'JavaScript',
  'Node.js', 'Express.js', 'Python',
  'Vercel', 'AWS Hosting', 'SSL Certificates', 'CDN Setup', 'Domain Management',
  'Stripe', 'PayPal', 'Razorpay', 'Mollie', 'Klarna',
  'Apple Pay', 'Google Pay', 'Samsung Pay', 'Cryptocurrency Payments',
  'Twilio SMS', 'AWS SNS', 'Firebase Messaging', 'AWS SES', 'SendGrid', 'WhatsApp API', 'Telegram Bot', 'Slack Integration', 
  'Swift', 'Kotlin', 'React Native', 'Flutter',
  'AWS', 'Google Cloud', 'Firebase', 
  'MongoDB', 'PostgreSQL', 'MySQL', 'Shopify Metafields',
  'Docker', 'Kubernetes', 'CI/CD', 'GitHub Actions',
  'GraphQL', 'REST APIs', 'WebSockets', 'OAuth', 'JWT Authentication',
  'Tailwind CSS', 'Material-UI', 'Bootstrap', 'Styled Components', 'SCSS'
];



const processSteps = [
  {
    icon: <DesignServicesIcon sx={{ fontSize: 40 }} />,
    title: 'Discovery & Design',
    description: 'We analyze your needs and create detailed wireframes and prototypes',
    color: '#8b5cf6'
  },
  {
    icon: <BuildIcon sx={{ fontSize: 40 }} />,
    title: 'Development',
    description: 'Our experts bring your vision to life with clean, scalable code',
    color: '#14b8a6'
  },
  {
    icon: <LaunchIcon sx={{ fontSize: 40 }} />,
    title: 'Launch & Support',
    description: 'We deploy your solution and provide ongoing maintenance and support',
    color: '#f97316'
  }
];

const Home = () => {
  const theme = useTheme();
  const [showScrollTop, setShowScrollTop] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <SEOHead
        title="DevHopes Labs - Expert Mobile & Web Development Solutions"
        description="Professional mobile app development, web development, and Shopify solutions. We transform your ideas into exceptional digital experiences with iOS, Android, React.js, and Next.js expertise."
        keywords="mobile app development, web development, iOS development, Android development, React.js, Next.js, Shopify development, custom software, DevHopes Labs, Netherlands, professional development services, mobile apps, web applications, Swift, Kotlin, TypeScript, JavaScript, cloud services, AWS, Firebase"
        url="/"
        type="website"
        schemaType="WebPage"
      />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)',
          color: 'white',
          py: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
          mt: { xs: 7, md: 8 },
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Stack spacing={{ xs: 2.5, md: 3 }}>
                  <Chip
                    label="✨ Building Digital Dreams"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 600,
                      alignSelf: 'flex-start',
                      px: 2,
                      py: 0.5,
                    }}
                  />
                  <MotionTypography
                    variant="h1"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.2rem' },
                      lineHeight: 1.1,
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 2,
                    }}
                  >
                    Transform Ideas Into
                    <br />
                    <Box 
                      component="span" 
                      sx={{ 
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Digital Reality
                    </Box>
                  </MotionTypography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 400, 
                      fontSize: { xs: '1.1rem', md: '1.5rem' },
                      opacity: 0.9,
                      lineHeight: 1.6,
                      maxWidth: 500,
                    }}
                  >
                    Expert mobile & web development solutions that drive growth and delight users
                  </Typography>
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={2} 
                    sx={{ mt: { xs: 3, md: 4 } }}
                  >
                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="contained"
                      size="large"
                      startIcon={<RocketLaunchIcon />}
                      sx={{
                        bgcolor: 'rgba(20, 184, 166, 0.9)', // Teal with opacity
                        color: '#ffffff',
                        px: { xs: 3, md: 4 },
                        py: { xs: 1.5, md: 2 },
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'rgba(20, 184, 166, 1)', // Full opacity on hover
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Start Your Project
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/portfolio"
                      variant="contained"
                      size="large"
                      startIcon={<WorkIcon />}
                      sx={{
                        bgcolor: 'rgba(99, 102, 241, 0.9)', // Indigo with opacity
                        color: '#ffffff',
                        px: { xs: 3, md: 4 },
                        py: { xs: 1.5, md: 2 },
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'rgba(99, 102, 241, 1)', // Full opacity on hover
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      View Portfolio
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/services"
                      variant="contained"
                      size="large"
                      startIcon={<DesignServicesIcon />}
                      sx={{
                        bgcolor: 'rgba(249, 115, 22, 0.9)', // Orange with opacity
                        color: '#ffffff',
                        px: { xs: 3, md: 4 },
                        py: { xs: 1.5, md: 2 },
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'rgba(249, 115, 22, 1)', // Full opacity on hover
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      View Services
                    </Button>
                  </Stack>
                </Stack>
              </MotionBox>
            </Grid>
            <Grid item>
              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mt: { xs: 4, md: 0 },
                  position: 'relative',
                }}
              >
                {/* Modern Tech Stack Display */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 3,
                    mb: 4,
                    width: '100%',
                    maxWidth: 400,
                  }}
                >
                  {[
                    { icon: <MobileIcon />, label: 'Mobile', color: '#0f766e' },
                    { icon: <WebIcon />, label: 'Web', color: '#f97316' },
                    { icon: <CloudIcon />, label: 'Cloud', color: '#8b5cf6' }
                  ].map((tech, index) => (
                    <MotionBox
                      key={tech.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                      whileHover={{ 
                        scale: 1.05,
                        transition: { duration: 0.2 }
                      }}
                      sx={{
                        textAlign: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 3,
                          background: `linear-gradient(135deg, ${tech.color}15 0%, ${tech.color}25 100%)`,
                          border: `2px solid ${tech.color}30`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          mx: 'auto',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: `linear-gradient(135deg, ${tech.color}25 0%, ${tech.color}35 100%)`,
                            borderColor: `${tech.color}50`,
                            transform: 'translateY(-2px)',
                          }
                        }}
                      >
                        {React.cloneElement(tech.icon, {
                          sx: { fontSize: 32, color: tech.color }
                        })}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          fontSize: '0.9rem',
                        }}
                      >
                        {tech.label}
                      </Typography>
                    </MotionBox>
                  ))}
                </Box>

                {/* Stats or Features */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 4,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    mt: 2,
                  }}
                >
                  {stats.map((stat, index) => (
                    <MotionBox
                      key={stat.label}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
                      sx={{
                        textAlign: 'center',
                        minWidth: 80,
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          background: 'linear-gradient(135deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.5,
                          fontSize: { xs: '1.8rem', md: '2.2rem' },
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.85rem',
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </MotionBox>
                  ))}
                </Box>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <MotionBox
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  sx={{ textAlign: 'center' }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    fontWeight={500}
                    sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}
                  >
                    {stat.label}
                  </Typography>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 3,
                background: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Why Choose DevHopes Labs?
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 600, 
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.5rem' },
                lineHeight: 1.6,
              }}
            >
              We combine technical expertise with creative vision to deliver exceptional results
            </Typography>
          </MotionBox>
          
          <Grid container spacing={6} justifyContent="center">
            {features.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <MotionBox
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  sx={{ 
                    textAlign: 'center',
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    '&::after': index < processSteps.length - 1 ? {
                      content: '""',
                      position: 'absolute',
                      top: { xs: '100%', md: '40%' },
                      right: { xs: '50%', md: '-30px' },
                      transform: { xs: 'translateX(50%)', md: 'translateY(-50%)' },
                      width: { xs: '2px', md: '60px' },
                      height: { xs: '30px', md: '2px' },
                      bgcolor: 'primary.300',
                      display: { xs: 'none', md: 'block' },
                      zIndex: 1
                    } : {}
                  }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 80, md: 100 },
                      height: { xs: 80, md: 100 },
                      bgcolor: step.color,
                      mx: 'auto',
                      mb: 3,
                      background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}dd 100%)`,
                      boxShadow: `0 8px 24px ${step.color}40`,
                    }}
                  >
                    {step.icon}
                  </Avatar>
                  
                  <Typography
                    variant="h5"
                    sx={{ 
                      fontWeight: 600, 
                      mb: 2,
                      fontSize: { xs: '1.3rem', md: '1.4rem' },
                      color: 'text.primary'
                    }}
                  >
                    {step.title}
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                      lineHeight: 1.7,
                      fontSize: { xs: '1rem', md: '1rem' },
                      maxWidth: 300,
                      mx: 'auto',
                      textAlign: 'center'
                    }}
                  >
                    {step.description}
                  </Typography>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Technologies Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ 
              fontWeight: 600, 
              mb: { xs: 4, md: 6 },
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Technologies We Master
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: { xs: 1.5, md: 2 },
              maxWidth: 800,
              mx: 'auto',
            }}
          >
            {technologies.map((tech, index) => (
              <motion.div
                key={tech}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Chip
                  label={tech}
                  sx={{
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.5, md: 1 },
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    fontWeight: 500,
                    bgcolor: 'primary.50',
                    color: 'primary.main',
                    border: '1px solid',
                    borderColor: 'primary.200',
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: 'primary.100',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                />
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>



      {/* Process Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 3,
                background: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Development Process
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 600, 
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.5rem' },
                lineHeight: 1.6,
              }}
            >
              A proven methodology that ensures successful project delivery
            </Typography>
          </MotionBox>
          
          <Grid container spacing={6} justifyContent="center">
            {processSteps.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <MotionBox
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  sx={{ 
                    textAlign: 'center',
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    '&::after': index < processSteps.length - 1 ? {
                      content: '""',
                      position: 'absolute',
                      top: { xs: '100%', md: '40%' },
                      right: { xs: '50%', md: '-30px' },
                      transform: { xs: 'translateX(50%)', md: 'translateY(-50%)' },
                      width: { xs: '2px', md: '60px' },
                      height: { xs: '30px', md: '2px' },
                      bgcolor: 'primary.300',
                      display: { xs: 'none', md: 'block' },
                      zIndex: 1
                    } : {}
                  }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 80, md: 100 },
                      height: { xs: 80, md: 100 },
                      bgcolor: step.color,
                      mx: 'auto',
                      mb: 3,
                      background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}dd 100%)`,
                      boxShadow: `0 8px 24px ${step.color}40`,
                    }}
                  >
                    {step.icon}
                  </Avatar>
                  
                  <Typography
                    variant="h5"
                    sx={{ 
                      fontWeight: 600, 
                      mb: 2,
                      fontSize: { xs: '1.3rem', md: '1.4rem' },
                      color: 'text.primary'
                    }}
                  >
                    {step.title}
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                      lineHeight: 1.7,
                      fontSize: { xs: '1rem', md: '1rem' },
                      maxWidth: 300,
                      mx: 'auto',
                      textAlign: 'center'
                    }}
                  >
                    {step.description}
                  </Typography>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center' }}
          >
            <AutoAwesomeIcon sx={{ fontSize: { xs: 50, md: 60 }, mb: 3, color: '#14b8a6' }} />
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 3,
              }}
            >
              Ready to Build Something Amazing?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: { xs: 4, md: 6 }, 
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.5rem' },
                lineHeight: 1.6,
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Let's turn your vision into a digital masterpiece that drives results
            </Typography>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={3} 
              justifyContent="center"
            >
              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: '#14b8a6',
                  color: 'white',
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: '#0f766e',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Start Your Project
              </Button>
              <Button
                component={RouterLink}
                to="/services"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                View Services
              </Button>
            </Stack>
          </MotionBox>
        </Container>
      </Box>

      {/* Scroll to Top Button */}
      <Zoom in={showScrollTop}>
        <Fab
          color="primary"
          size="medium"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            zIndex: 1000,
            background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0f766e 0%, #134e4a 100%)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s ease',
            boxShadow: '0 8px 32px rgba(20, 184, 166, 0.3)',
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </>
  );
};

export default Home; 